import React, { useState, useRef, useEffect } from 'react';
import {
  IconButton
} from '@mui/material';
import {
  ChevronRight,
  Add,
  Chat,
} from '@mui/icons-material';
import { useChatContext } from '../context/ChatContext';
import { ConfirmDialog, RenameDialog } from './dialogs';
import UserProfile from './UserProfile';

const Sidebar: React.FC = () => {
  const {
    uiState,
    appState,
    toggleSidebar,
    loadChat,
    deleteChat,
    switchToLanding,
    toggleChatStar,
    renameChat,
    showAllChatsView
  } = useChatContext();

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);
  const [hoveredMenuOption, setHoveredMenuOption] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ [key: string]: { top: number; showAbove: boolean } }>({});
  
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [pendingDeleteChatId, setPendingDeleteChatId] = useState<string | null>(null);
  const [pendingRenameChatId, setPendingRenameChatId] = useState<string | null>(null);
  const [renameInitialValue, setRenameInitialValue] = useState('');
  
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // Calculate menu position based on button position
  const calculateMenuPosition = (buttonElement: HTMLButtonElement) => {
    const buttonRect = buttonElement.getBoundingClientRect();
    const menuHeight = 120; // Approximate menu height
    const buffer = 120; // Buffer from viewport edge
    
    // Check if there's enough space below the button
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const showAbove = spaceBelow < menuHeight + buffer;
    
    // Position the menu: 42px below button (or 123px above if showAbove)
    const top = showAbove ? -menuHeight - 13 : 42;
    
    return { top, showAbove };
  };

  // Handle menu toggle with position calculation
  const handleMenuToggle = (chatId: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    
    if (openMenuId === chatId) {
      setOpenMenuId(null);
      return;
    }
    
    const buttonElement = e.currentTarget;
    const position = calculateMenuPosition(buttonElement);
    
    setMenuPosition(prev => ({
      ...prev,
      [chatId]: position
    }));
    setOpenMenuId(chatId);
  };

  // Close menus when clicking outside
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (openMenuId && menuRefs.current[openMenuId]) {
        if (!menuRefs.current[openMenuId]?.contains(e.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openMenuId]);

  // Monitor savedChats changes
  useEffect(() => {
    // console.log(`[Sidebar] useEffect - savedChats changed. Total: ${appState.savedChats.length}, Starred: ${appState.savedChats.filter(c => c.isStarred).length}`);
  }, [appState.savedChats]);

  const handleNewChat = () => {
    switchToLanding();
  };

  const handleChatClick = (chatId: string) => {
    loadChat(chatId);
  };

  const handleDeleteChat = (chatId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingDeleteChatId(chatId);
    setDeleteDialogOpen(true);
  };

  const handleRenameChat = (chatId: string, currentTitle: string) => {
    setPendingRenameChatId(chatId);
    setRenameInitialValue(currentTitle);
    setRenameDialogOpen(true);
  };

  const confirmDelete = () => {
    if (pendingDeleteChatId) {
      deleteChat(pendingDeleteChatId);
    }
    setDeleteDialogOpen(false);
    setPendingDeleteChatId(null);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setPendingDeleteChatId(null);
  };

  const confirmRename = (newTitle: string) => {
    if (pendingRenameChatId && newTitle.trim()) {
      renameChat(pendingRenameChatId, newTitle.trim());
    }
    setRenameDialogOpen(false);
    setPendingRenameChatId(null);
    setRenameInitialValue('');
  };

  const cancelRename = () => {
    setRenameDialogOpen(false);
    setPendingRenameChatId(null);
    setRenameInitialValue('');
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Split chats into starred and non-starred
  const starredChats = appState.savedChats.filter(chat => chat.isStarred);
  const nonStarredChats = appState.savedChats.filter(chat => !chat.isStarred);
  
  // Debug logging
  // console.log(`[Sidebar] Render - Total chats: ${appState.savedChats.length}, Starred: ${starredChats.length}, Non-starred: ${nonStarredChats.length}`);

  const handleShowAllChats = () => {
    showAllChatsView();
  };

  return (
    <>
      <div
        className={`sidebar-container ${uiState.sidebarCollapsed ? 'collapsed' : ''}`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: uiState.sidebarCollapsed ? '60px' : '300px',
          zIndex: 10,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid var(--neutral-light)',
          backgroundColor: '#FFFFFF'
        }}
      >
        {/* Sidebar Loading Overlay - for update operations */}
        {uiState.sidebarLoading && (
          <div className="sidebar-loading-overlay">
            <img
              src="/images/belle-spinner.gif"
              alt="Loading..."
              className="belle-spinner"
            />
            <div className="loading-text">
              Updating chat...
            </div>
          </div>
        )}

        {/* Initial Chats Loading Overlay - only blocks sidebar */}
        {appState.isLoadingChats && !uiState.sidebarCollapsed && (
          <div className="sidebar-loading-overlay">
            <img
              src="/images/belle-spinner.gif"
              alt="Loading..."
              className="belle-spinner"
            />
            <div className="loading-text">
              Loading saved chats...
            </div>
          </div>
        )}

        {/* Header */}
        <div
          className="sidebar-header"
          style={{ justifyContent: 'flex-start', paddingLeft: 8 }}
        >
          <IconButton
            onClick={toggleSidebar}
            className="sidebar-toggle-btn"
            style={{
              transform: uiState.sidebarCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.3s'
            }}
          >
            <ChevronRight />
          </IconButton>
          {!uiState.sidebarCollapsed && (
            <div className="sidebar-title">
              Ask Bell
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px',
          position: 'relative'
        }}>
          {/* Navigation Links */}
          <div style={{ padding: 0 }}>
            <div style={{ padding: 0 }}>
              <div
                className="sidebar-link"
                onClick={handleNewChat}
                style={{
                  justifyContent: uiState.sidebarCollapsed ? 'center' : 'flex-start',
                  cursor: 'pointer'
                }}
              >
                <Add />
                {!uiState.sidebarCollapsed && (
                  <span>New chat</span>
                )}
              </div>
            </div>
            <div style={{ padding: 0 }}>
              <div
                className="sidebar-link"
                style={{
                  justifyContent: uiState.sidebarCollapsed ? 'center' : 'flex-start',
                  cursor: 'pointer'
                }}
                onClick={handleShowAllChats}
              >
                <Chat />
                {!uiState.sidebarCollapsed && (
                  <span>Chats</span>
                )}
              </div>
            </div>
          </div>

          {/* Starred Chats Section */}
          {!uiState.sidebarCollapsed && starredChats.length > 0 && (
            <>
              <div className="sidebar-section-header">Starred</div>
              <div style={{ position: 'relative', padding: 0, marginBottom: 0 }}>
                {starredChats.slice(0, 10).map((chat, idx, arr) => (
                  <div key={chat.id} style={{ padding: 0 }}>
                    <div
                      className="chat-item"
                      onClick={() => handleChatClick(chat.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '4px 8px',
                        minHeight: 36,
                        cursor: 'pointer',
                        borderRadius: 6,
                        marginBottom: idx === arr.length - 1 ? 0 : 2,
                        transition: 'background 0.2s',
                        width: '100%',
                      }}
                      onMouseEnter={() => setHoveredChatId(chat.id)}
                      onMouseLeave={() => setHoveredChatId(null)}
                    >
                      <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div className="chat-item-title" style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: 15,
                          fontWeight: 500
                        }}>
                          {chat.title}
                        </div>
                        <div className="chat-item-time" style={{ fontSize: 12, color: '#888', marginTop: 1 }}>
                          {formatTimeAgo(chat.timestamp)}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, marginLeft: 'auto', display: 'flex', alignItems: 'center', position: 'relative' }}>
                        <button
                          className="chat-item-menu-btn"
                          aria-label="More actions"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: '0 4px',
                            height: 24,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 'unset',
                            opacity: (hoveredChatId === chat.id || openMenuId === chat.id) ? 1 : 0,
                            pointerEvents: (hoveredChatId === chat.id || openMenuId === chat.id) ? 'auto' : 'none',
                            transition: 'opacity 0.15s',
                          }}
                          onClick={e => handleMenuToggle(chat.id, e)}
                        >
                          <span className="material-icons" style={{ fontSize: 20, color: '#666', lineHeight: 1 }}>more_horiz</span>
                        </button>
                        {openMenuId === chat.id && (
                          <div
                            ref={el => { menuRefs.current[chat.id] = el; }}
                            className="chat-item-menu"
                            style={{
                              position: 'absolute',
                              top: menuPosition[chat.id]?.top ?? 42,
                              right: 0,
                              background: '#fff',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                              borderRadius: 8,
                              zIndex: 100,
                              minWidth: 140,
                              padding: '8px 0',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                            onClick={e => e.stopPropagation()}
                          >
                            <div
                              className="chat-item-menu-option"
                              style={{ 
                                padding: '8px 16px', 
                                cursor: 'pointer', 
                                fontSize: 15, 
                                color: '#222', 
                                whiteSpace: 'nowrap',
                                backgroundColor: hoveredMenuOption === `${chat.id}-unstar` ? '#f5f5f5' : 'transparent',
                                transition: 'background-color 0.15s'
                              }}
                              onMouseEnter={() => setHoveredMenuOption(`${chat.id}-unstar`)}
                              onMouseLeave={() => setHoveredMenuOption(null)}
                              onClick={async () => {
                                await toggleChatStar(chat.id);
                                setOpenMenuId(null);
                              }}
                            >
                              Unstar
                            </div>
                            <div
                              className="chat-item-menu-option"
                              style={{ 
                                padding: '8px 16px', 
                                cursor: 'pointer', 
                                fontSize: 15, 
                                color: '#222', 
                                whiteSpace: 'nowrap',
                                backgroundColor: hoveredMenuOption === `${chat.id}-rename` ? '#f5f5f5' : 'transparent',
                                transition: 'background-color 0.15s'
                              }}
                              onMouseEnter={() => setHoveredMenuOption(`${chat.id}-rename`)}
                              onMouseLeave={() => setHoveredMenuOption(null)}
                              onClick={() => {
                                setOpenMenuId(null);
                                handleRenameChat(chat.id, chat.title);
                              }}
                            >
                              Rename
                            </div>
                            <div
                              className="chat-item-menu-option"
                              style={{ 
                                padding: '8px 16px', 
                                cursor: 'pointer', 
                                fontSize: 15, 
                                color: '#E1502F', 
                                whiteSpace: 'nowrap',
                                backgroundColor: hoveredMenuOption === `${chat.id}-delete` ? '#fef2f2' : 'transparent',
                                transition: 'background-color 0.15s'
                              }}
                              onMouseEnter={() => setHoveredMenuOption(`${chat.id}-delete`)}
                              onMouseLeave={() => setHoveredMenuOption(null)}
                              onClick={() => {
                                setOpenMenuId(null);
                                setPendingDeleteChatId(chat.id);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              Delete
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Recent Chats Section */}
          {!uiState.sidebarCollapsed && (
            <>
              <div className="sidebar-section-header">
                Recents
              </div>
              <div style={{ position: 'relative', padding: 0, minHeight: 240, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {nonStarredChats.slice(0, 10).map((chat) => (
                  <div key={chat.id} style={{ padding: 0 }}>
                    <div
                      className="chat-item"
                      onClick={() => handleChatClick(chat.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '4px 8px',
                        minHeight: 36,
                        cursor: 'pointer',
                        borderRadius: 6,
                        marginBottom: 2,
                        transition: 'background 0.2s',
                        width: '100%',
                      }}
                      onMouseEnter={() => setHoveredChatId(chat.id)}
                      onMouseLeave={() => setHoveredChatId(null)}
                    >
                      <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div className="chat-item-title" style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: 15,
                          fontWeight: 500
                        }}>
                          {chat.title}
                        </div>
                        <div className="chat-item-time" style={{ fontSize: 12, color: '#888', marginTop: 1 }}>
                          {formatTimeAgo(chat.timestamp)}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, marginLeft: 'auto', display: 'flex', alignItems: 'center', position: 'relative' }}>
                        {(hoveredChatId === chat.id || openMenuId === chat.id) && (
                          <button
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 4,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginLeft: 8,
                              fontSize: 16,
                              color: '#666',
                            }}
                            onClick={e => handleMenuToggle(chat.id, e)}
                          >
                            •••
                          </button>
                        )}

                        {openMenuId === chat.id && (
                          <div
                            ref={el => { menuRefs.current[chat.id] = el; }}
                            className="chat-item-menu"
                            style={{
                              position: 'absolute',
                              top: menuPosition[chat.id]?.top ?? 42,
                              right: 0,
                              background: '#fff',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                              borderRadius: 8,
                              zIndex: 100,
                              minWidth: 140,
                              padding: '8px 0',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                            onClick={e => e.stopPropagation()}
                          >
                            <div
                              className="chat-item-menu-option"
                              style={{ 
                                padding: '8px 16px', 
                                cursor: 'pointer', 
                                fontSize: 15, 
                                color: '#222', 
                                whiteSpace: 'nowrap',
                                backgroundColor: hoveredMenuOption === `${chat.id}-star` ? '#f5f5f5' : 'transparent',
                                transition: 'background-color 0.15s'
                              }}
                              onMouseEnter={() => setHoveredMenuOption(`${chat.id}-star`)}
                              onMouseLeave={() => setHoveredMenuOption(null)}
                              onClick={async () => {
                                await toggleChatStar(chat.id);
                                setOpenMenuId(null);
                              }}
                            >
                              Star
                            </div>
                            <div
                              className="chat-item-menu-option"
                              style={{ 
                                padding: '8px 16px', 
                                cursor: 'pointer', 
                                fontSize: 15, 
                                color: '#222', 
                                whiteSpace: 'nowrap',
                                backgroundColor: hoveredMenuOption === `${chat.id}-rename-recent` ? '#f5f5f5' : 'transparent',
                                transition: 'background-color 0.15s'
                              }}
                              onMouseEnter={() => setHoveredMenuOption(`${chat.id}-rename-recent`)}
                              onMouseLeave={() => setHoveredMenuOption(null)}
                              onClick={() => {
                                setOpenMenuId(null);
                                handleRenameChat(chat.id, chat.title);
                              }}
                            >
                              Rename
                            </div>
                            <div
                              className="chat-item-menu-option"
                              style={{ 
                                padding: '8px 16px', 
                                cursor: 'pointer', 
                                fontSize: 15, 
                                color: '#e74c3c', 
                                whiteSpace: 'nowrap',
                                backgroundColor: hoveredMenuOption === `${chat.id}-delete-recent` ? '#fef2f2' : 'transparent',
                                transition: 'background-color 0.15s'
                              }}
                              onMouseEnter={() => setHoveredMenuOption(`${chat.id}-delete-recent`)}
                              onMouseLeave={() => setHoveredMenuOption(null)}
                              onClick={() => {
                                setOpenMenuId(null);
                                handleDeleteChat(chat.id, { stopPropagation: () => {} } as React.MouseEvent);
                              }}
                            >
                              Delete
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid var(--neutral-light)'
        }}>
          <UserProfile collapsed={uiState.sidebarCollapsed} />
        </div>
      </div>

      {/* Custom Dialogs */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Chat"
        message="Are you sure you want to delete this chat? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      <RenameDialog
        open={renameDialogOpen}
        title="Rename Chat"
        label="Chat title"
        initialValue={renameInitialValue}
        confirmText="Rename"
        cancelText="Cancel"
        onConfirm={confirmRename}
        onCancel={cancelRename}
      />
    </>
  );
};

export default Sidebar; 